import { lazy, type ComponentType } from 'react';

const RELOAD_GUARD_KEY = 'jam-chunk-reloaded-at';
const RELOAD_GUARD_MS = 10_000;

function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = err.message || '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    /Loading chunk \S+ failed/.test(msg)
  );
}

/**
 * After a deploy, the live `index.html` references new chunk hashes but
 * any already-open tab still has the old hashes in memory. The first
 * lazy-route nav after deploy throws "Failed to fetch dynamically
 * imported module" — Firebase Hosting's SPA rewrite then returns
 * `index.html` for the missing `.js`, which the browser refuses as
 * text/html. A full reload picks up the fresh index + new hashes.
 *
 * The guard prevents infinite reloads if the chunk is genuinely broken.
 */
export function lazyWithReload<T extends ComponentType<unknown>>(
  importer: () => Promise<{ default: T }>,
): ReturnType<typeof lazy<T>> {
  return lazy(() =>
    importer().catch((err) => {
      if (!isChunkLoadError(err) || typeof window === 'undefined') throw err;

      const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
      if (Date.now() - last < RELOAD_GUARD_MS) throw err;

      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
      window.location.reload();
      // Return a never-resolving promise so Suspense holds its fallback
      // while the reload is in flight — avoids the error surfacing first.
      return new Promise<{ default: T }>(() => {});
    }),
  );
}
